import { useEffect } from 'react';
import type { MutableRefObject } from 'react';
import type { ForceGraphMethods } from 'react-force-graph-2d';

/**
 * How far above the canvas centre the concept lands, in screen pixels: clear
 * of the peek card that rises over the bottom of the phone canvas.
 */
const PEEK_LIFT_PX = 72;
const GLIDE_MS = 400;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface UseGlideToSelectionOptions {
  graphRef: MutableRefObject<ForceGraphMethods | undefined>;
  /** The graph's nodes, positioned by the simulation (or pinned). */
  nodes: Array<{ id: string; x?: number; y?: number }>;
  /** The concept to bring into view; `null` stays put. */
  selectedId: string | null | undefined;
  /** Off on desktop, where the panel opens beside the graph instead. */
  enabled: boolean;
  /** Called as the camera moves - the auto-fit stands down, so a resize does
   *  not yank the view back off the concept. */
  onGlide: () => void;
}

/**
 * The phone graph's camera follows a tap: the tapped concept glides to the
 * centre, lifted above the peek card, at the zoom the user chose. Instant
 * under reduced motion.
 */
export function useGlideToSelection({
  graphRef,
  nodes,
  selectedId,
  enabled,
  onGlide,
}: UseGlideToSelectionOptions) {
  useEffect(() => {
    if (!enabled || !selectedId) return;
    const fg = graphRef.current;
    const node = nodes.find(n => n.id === selectedId);
    if (!fg || node?.x == null || node.y == null) return;
    onGlide();
    const ms = prefersReducedMotion() ? 0 : GLIDE_MS;
    fg.centerAt(node.x, node.y + PEEK_LIFT_PX / fg.zoom(), ms);
  }, [graphRef, nodes, selectedId, enabled, onGlide]);
}
